import React from 'react';
import { TouchableOpacity, View } from 'react-native';

import {
  ErrorScreen,
  Icon,
  IconTypes,
  VFText,
  Card,
  Badge,
} from '@vfgroup-oneplatform/foundation/Components';
import { LightThemeColors, withTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';
import { dateCurrencyFromatter } from '@vfgroup-oneplatform/foundation/Utils/dateCurrencyFormate';
import { isFunction as _isFunction, isEmpty as _isEmpty } from 'lodash';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';

import { getThemeImages } from '../../Themes';
import { ServiceToImageMapper, DateFormatters, Helpers, BillingTrackingManager } from '../../Utils';
import BillingTestKeys from '../../Utils/BillingTestKeys.js';
import ImageList from '../ImageList';
import VfView from '../VfView';

import { Bill_Status } from '../../Utils/BillStatus.Enum'

import styles from './CurrentBillCard.Styles';
import CurrentBillCardLoading from './CurrentBillCardLoading/CurrentBillCardLoading';

function getDateFormatters(getTimePeriod, getYear, getMonth) {
  const _getTimePeriod = _isFunction(getTimePeriod) ? getTimePeriod : DateFormatters.getTimePeriod;
  const _getYear = _isFunction(getYear) ? getYear : DateFormatters.getYear;
  const _getMonth = _isFunction(getMonth) ? getMonth : DateFormatters.getMonth;

  return {
    getTimePeriod: _getTimePeriod,
    getYear: _getYear,
    getMonth: _getMonth,
  };
}

function trackNegativeInfoModal(additionalData) {
  BillingTrackingManager.trackEvent('ui_interaction', {
    event_action: 'OnClick',
    event_category: 'Button',
    event_label: 'information',
    page_name: 'My Bills',
    page_section: 'Billing:Negative Bill Quick Action',
    ...additionalData,
  });
}



const getDateFormattersMemoized = memoize(getDateFormatters);
function CurrentBillCard({
  isLoading,
  loader,
  imageListProp,
  onPress,
  startDate,
  endDate,
  value,
  currency,
  isCurrencyFirst,
  extraChargesText,
  chargesList,
  isStartDateBillDate,
  getYear,
  getMonth,
  getTimePeriod,
  toggleModal,
  renderHelperView,
  isError,
  onTryAgainPress,
  theme,
  issueDate,
  withBillDate,
  getAdditionalTrackingInfo,
  withTimePeriodDisplayed,
  withBillNegativeAmount,
  currentBillAmountStyle,
  billingNegativeValueNote,
  withBadge,
  status,
  paymentInfo,
  address,
  ...props
}) {
  const Loader = loader ? loader : <CurrentBillCardLoading />;

  const images = ServiceToImageMapper(imageListProp, theme.name);
  const themeImages = getThemeImages(theme.name);

  const getNegativeBillStyle = billValue =>
    billValue < 0 ? styles.negativeBill(theme) : undefined;

  const dataFormatters = getDateFormattersMemoized(getTimePeriod, getYear, getMonth);

  const billDate = issueDate ? issueDate : isStartDateBillDate ? startDate : endDate;
  const year = dataFormatters.getYear(billDate);
  const month = dataFormatters.getMonth(billDate);
  const timePeriod = dataFormatters.getTimePeriod(startDate, endDate);
  const onNegativeBillIconPress = () => {
    toggleModal();
    const additionalData = _isFunction(getAdditionalTrackingInfo) && getAdditionalTrackingInfo();
    trackNegativeInfoModal(additionalData);
  };

  const getHintTextProps = () => {
    const textProps = {
      text: '',
      placeHolders: '',
      type: 'neutral'
    }

    if (status === Bill_Status.SETTLED) {
      textProps.i18nKey = 'billing_card_tag_paid'
      textProps.type = 'positive'
    } else {
      if (!_isEmpty(paymentInfo)) {
        textProps.i18nKey = 'billing_credit_card_paid_date'
        textProps.placeHolders = paymentInfo
        textProps.type = 'neutral'
      }
    }
    return textProps
  }


  const renderErrorScreen = (
    <ErrorScreen
      backgroundStyle={styles.errorBackgroundStyle}
      iconStyle={styles.errorIconStyle}
      iconName="ic_warning_mid"
      text="billing_current_bill_error_text"
      textButton="billing_current_bill_error_try_again_button"
      textStyle={styles.errorTextStyle}
      tryButtonContainerStyle={styles.errorTryButtonContainerStyle}
      onPress={onTryAgainPress}
    />
  );
  const renderComponent = (
    <>
      <VfView >
        <View style={styles.currentBillHeader(withBillDate)}>
          {withBillDate && (
            <VFText
              i18nKey={withTimePeriodDisplayed ? `${year} | ${timePeriod}` : `${year}`}
              style={styles.year(theme)}
              testKey={BillingTestKeys.currentBillYearText}
            />
          )}
          {withBadge && paymentInfo && paymentInfo.length !== 0 && <VfView style={styles.marginBetweenComponents}>
            <VfView row justify="space-between" alignItems="flex-end">
              <Badge
                {...getHintTextProps()}
              />
            </VfView>
          </VfView>}
          {renderHelperView
            ? renderHelperView({
              isLoading,
              startDate,
              endDate,
              value,
              currency,
              isCurrencyFirst,
              extraChargesText,
              chargesList,
              isStartDateBillDate,
            })
            : value < 0 && (
              <TouchableOpacity
                onPress={onNegativeBillIconPress}
                activeOpacity={1}
                hitSlop={{
                  top: 10,
                  left: 10,
                  bottom: 10,
                  right: 10,
                }}
                testID={BillingTestKeys.currentBillNegativeBalanceView}
              >
                <VfView style={styles.negativeBalanceContainer} row>
                  <VFText
                    i18nKey={billingNegativeValueNote || 'billing_negative_value_note'}
                    style={styles.negativeBalanceText(theme)}
                    testKey={BillingTestKeys.currentBillNegativeBalanceText}
                  />
                  <Icon
                    source={themeImages.icInfoRed}
                    type={IconTypes.image}
                    size={22}
                    style={styles.icon}
                    testID={BillingTestKeys.currentBillNegativeIcon}
                  />
                </VfView>
              </TouchableOpacity>
            )}
        </View>
        {withBillDate && (
          <VFText
            style={styles.month(theme)}
            i18nKey={`${month}`}
            testKey={BillingTestKeys.currentBillMonthText}
          />
        )}
        <VfView style={styles.price} row alignItems="baseline">
          {isCurrencyFirst ? (
            <>
              <VFText
                style={[styles.currency(theme), currentBillAmountStyle]}
                i18nKey={`${currency}`}
                testKey={BillingTestKeys.currentBillCurrencyText}
              />
              <VFText
                style={[styles.priceText(theme), currentBillAmountStyle]}
                i18nKey={dateCurrencyFromatter.currencyTransformer(
                  withBillNegativeAmount ? value : Math.abs(value),
                )}
                testKey={BillingTestKeys.currentBillPriceText}
              />
            </>
          ) : (
            <>
              <VFText
                i18nKey={dateCurrencyFromatter.currencyTransformer(
                  withBillNegativeAmount ? value : Math.abs(value),
                )}
                testKey={BillingTestKeys.currentBillPriceTextFixed}
                style={[styles.priceText(theme), currentBillAmountStyle]}
              />
              <VFText
                i18nKey={`${currency}`}
                style={[styles.currency(theme), currentBillAmountStyle]}
                testKey={BillingTestKeys.currentBillCurrencyText}
              />
            </>
          )}
        </VfView>
      </VfView>
      <View style={styles.lineBottom(theme)} />

      {(extraChargesText || chargesList) && (
        <VfView
          justify="center"
          style={styles.extraChargesContainer}
          testID={BillingTestKeys.extraChargesContainer}
        >
          {extraChargesText && (
            <VfView style={styles.extraChargesLabel}>
              <VFText
                testKey={BillingTestKeys.currentBillExtraChargesText}
                i18nKey={`${extraChargesText}`}
                style={styles.extraChargesText(theme)}
              />
            </VfView>
          )}
          {chargesList &&
            chargesList.map((charge, i) => {
              return (
                <VfView
                  key={charge.title + i}
                  row
                  justify="space-between"
                  style={styles.extraChargeItem}
                  testID={BillingTestKeys.extraChargeItemContainer}
                >
                  <VFText
                    i18nKey={`${charge.title}`}
                    style={[
                      styles.extraRoamingText(theme),
                      getNegativeBillStyle(Helpers.splitStringBySpace(charge.price)[0]),
                    ]}
                    testKey={BillingTestKeys.extraChargeItemTextContainer}
                  />
                  <VFText
                    i18nKey={charge.price}
                    style={[
                      styles.extraRoamingText(theme),
                      getNegativeBillStyle(Helpers.splitStringBySpace(charge.price)[0]),
                    ]}
                    testKey={BillingTestKeys.extraChargeItemText}
                  />
                </VfView>
              );
            })}
        </VfView>
      )}

      {address && <VFText
        i18nKey={address}
        style={[
          styles.extraRoamingText(theme),
          { marginBottom: 5 }
        ]}
        testKey={BillingTestKeys.extraChargeItemTextContainer}
      />}

      {imageListProp && (
        <ImageList
          images={images}
          {...imageListProp}
          title={'billing_current_bill_included_services'}
        />
      )}
    </>
  );
  return (
    <Card
      onPressCard={() => {
        if (isLoading) {
          return;
        }
        onPress({
          isLoading,
          isError,
          imageListProp,
          startDate,
          endDate,
          year,
          month,
          timePeriod,
          value,
          currency,
          isCurrencyFirst,
          extraChargesText,
          chargesList,
          theme,
          issueDate,
          withBillNegativeAmount,
          ...props,
        });
      }}
      style={styles.cardStyle(theme)}
      testID={BillingTestKeys.currentBillCard}
    >
      <VfView justify="center" alignItems="center">
        {isLoading ? (
          Loader
        ) : (
          <VfView testID={BillingTestKeys.currentBillCardContainerView} style={styles.container}>
            {isError ? renderErrorScreen : renderComponent}
          </VfView>
        )}
      </VfView>
    </Card>
  );
}

CurrentBillCard.defaultProps = {
  getYear: DateFormatters.getYear,
  getMonth: DateFormatters.getMonth,
  getTimePeriod: DateFormatters.getTimePeriod,
  isStartDateBillDate: false,
  theme: LightThemeColors,
  withBillDate: true,
};

CurrentBillCard.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currency: PropTypes.string,
  isCurrencyFirst: PropTypes.bool,
  chargesList: PropTypes.array,
  isLoading: PropTypes.bool,
  withBillDate: PropTypes.bool,
  loader: PropTypes.node,
  imageListProp: PropTypes.object,
  onPress: PropTypes.func,
  extraChargesText: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  getYear: PropTypes.func,
  getMonth: PropTypes.func,
  getTimePeriod: PropTypes.func,
  isStartDateBillDate: PropTypes.bool,
  toggleModal: PropTypes.func,
  renderHelperView: PropTypes.func,
  isError: PropTypes.bool,
  onTryAgainPress: PropTypes.func,
  theme: PropTypes.object,
  issueDate: PropTypes.string,
  getAdditionalTrackingInfo: PropTypes.func,
  withTimePeriodDisplayed: PropTypes.bool,
  withBillNegativeAmount: PropTypes.bool,
  billingNegativeValueNote: PropTypes.string,
  currentBillAmountStyle: PropTypes.object,
};

export default withTheme(CurrentBillCard);
